import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Commands, HomeTabs, Results, Search, SessionInfo } from '@local/client-contracts';
import { isLayoutWidget, isMac } from '@local/common-web';
import { PopupService } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ScrollServiceModel } from '@shared/models/scroll-service-model.interface';
import { CustomKeyboardEvent, KeyboardService } from '@shared/services/keyboard.service';
import { SessionService } from '@shared/services/session.service';
import { windowSizeObserver } from '@shared/utils';
import { NgScrollbar } from 'ngx-scrollbar';
import { SearchResultContext, SearchSession } from 'src/app/bar/services/search';
import { KeyboardHelperService } from '../../../../shared/helper/keyboard-helper.service';
import { CollectionItemComponent } from '../../collections-page/components/collection-item/collection-item.component';
import { GoLinksViewItemComponent } from '../../results-views/go-links/go-links-view-item/go-links-view-item.component';
import { PeopleViewItemComponent } from '../../results-views/people/people-view-item/people-view-item.component';
import { ResultItemComponent } from '../../results/components/result-item/result-item.component';
import { SearchResults } from '../../results/models/results-types';
import { ScrollService } from '../../results/services/scroll.service';
import { WidgetsService } from '../services/widgets.service';
import { ReplaySubject, takeUntil } from 'rxjs';
import { ManualPromise } from '@local/common';
import { ContextMenuComponent, ContextMenuData, ContextMenuItem, ContextMenuService } from '@shared/components';
import { PopupRef } from '@local/ui-infra';
import { WorkspacesService } from 'src/app/bar/services';
import { AppPopupComponent, AppPopupData } from 'src/app/bar/components/app-popup/app-popup.component';
import { EventsService } from '@shared/services';
import { RouterService } from '@shared/services/router.service';
import { isEqual } from 'lodash';
import { BaseWidget } from 'gridstack/dist/angular';
import { HomeTabsService } from 'src/app/bar/services/home-tabs.service';
import { AvatarListService } from 'src/app/bar/services/avatar-list.service';
import { KeyName } from '@local/ts-infra';

interface UpdatedWidgetOption {
  sort?: boolean;
  layout?: boolean;
}

@UntilDestroy()
@Component({
  template: '',
})
export abstract class WidgetBaseComponent<T extends HomeTabs.Widget>
  extends BaseWidget
  implements OnInit, AfterViewInit, OnDestroy, ScrollServiceModel
{
  private readonly WARNING_DATA: AppPopupData = {
    message: 'Changing the view of this widget will <br> be displayed on the homepages of all <br> users who have access.',
    showButtons: true,
    rightButtonStyle: { type: 'primary' },
    templateContentPosition: 'bottom',
    content: {
      secondaryButton: 'Discard',
      primaryButton: 'Continue',
    },
    messageStyle: { fontSize: '14px' },
  };
  private keyHandlerId: string;
  private keyboardHelperService: KeyboardHelperService;
  private _isOwnerOrAdmin: boolean;
  private _accountId: string;
  private warningPopupRef: PopupRef<AppPopupComponent, AppPopupData>;

  protected currentSession: SessionInfo;
  protected pageType: string;
  protected isLoading: boolean;
  protected isFocused: boolean;
  protected heightWidget: number;
  protected isSmallScreen: boolean;
  protected minWidgetSize = 280;
  protected widgetRowHeight = 52;
  protected scrollService: ScrollService<SearchResults>;
  protected keyboardOn = false;
  protected selectedIndex: number;
  protected displayedContext: SearchResultContext;
  protected activeContextMenu: PopupRef<ContextMenuComponent, ContextMenuData>;
  protected contextMenuOpened: boolean;
  protected layoutMode: Results.LayoutType = 'list';
  protected saveStateImmediate = false;
  protected widgetResizeChange$: ReplaySubject<number> = new ReplaySubject(1);
  protected scrollHeight: string;
  protected currentSort: Search.Sort;
  protected prevState: { type: string; selectedIndex: number } = { type: undefined, selectedIndex: undefined };
  protected searchSession: SearchSession;
  protected movedWidget = true;
  protected afterViewInitPromise = new ManualPromise<void>();
  protected currentTab: HomeTabs.HomeTab;
  protected settings: T;

  @Input() widgetId: string;

  //keyboard
  @ViewChild(CdkVirtualScrollViewport) scrollViewport: CdkVirtualScrollViewport;
  @ViewChild(NgScrollbar) scrollbarRef: NgScrollbar;
  @ViewChildren('resultItem', { read: ElementRef }) resultElementsRef: QueryList<ElementRef>;
  @ViewChildren('resultItem') resultElementsComponents: QueryList<
    ResultItemComponent | GoLinksViewItemComponent | CollectionItemComponent | PeopleViewItemComponent
  >;

  @ViewChild('warningPopupContent') warningPopupContent: ElementRef;
  @ViewChild('widget', { read: ElementRef }) widgetRef: ElementRef;

  protected get noScroll(): boolean {
    return this.displayedContext?.items?.length * this.widgetRowHeight < this.heightWidget;
  }

  protected get itemHeights() {
    if (!this.displayedContext?.items?.length) {
      return [];
    }
    return Array(this.displayedContext.items.length).fill(44);
  }

  protected get scrollViewEl(): HTMLElement {
    return this.scrollViewport?.elementRef?.nativeElement;
  }

  protected get isOwnerOrAdmin(): boolean {
    return this._isOwnerOrAdmin;
  }

  protected get accountId(): string {
    return this._accountId;
  }

  protected get canEdit(): boolean {
    return this.isOwnerOrAdmin;
  }

  constructor(
    protected elementRef: ElementRef,
    protected cdr: ChangeDetectorRef,
    protected keyboardService: KeyboardService,
    protected widgetsService: WidgetsService,
    protected ngZone: NgZone,
    protected session: SessionService,
    protected contextMenuService: ContextMenuService,
    protected workspacesService: WorkspacesService,
    protected popupService: PopupService,
    protected eventsService: EventsService,
    protected routerService: RouterService,
    protected homeTabsService: HomeTabsService,
    protected avatarListService: AvatarListService
  ) {
    super();
    this.workspacesService.current$.subscribe((workspace) => {
      this._accountId = workspace?.accountId;
      this._isOwnerOrAdmin = workspace?.isOwner || workspace?.isAdmin;
    });
  }

  ngOnInit(): void {
    this.widgetsService
      .getWidgetSettings$<T>(this.widgetId)
      .pipe(untilDestroyed(this))
      .subscribe((settings) => {
        if (!isEqual(this.settings, settings)) {
          this.settings = settings;
          if (settings) {
            this.initData(true);
          }
        }
      });

    this.setRowData();
    this.setUpKeyboardHelperService();
    this.scrollService = new ScrollService(this.ngZone);
    this.widgetsService.triggerWidgetFocus$.pipe(untilDestroyed(this)).subscribe((id: string) => {
      if (this.settings.id === id) {
        this.isFocused = true;
        this.cdr.markForCheck();
      } else {
        this.isFocused = false;
        this.cdr.markForCheck();
      }
    });
    this.homeTabsService.all$.pipe(untilDestroyed(this)).subscribe((all) => {
      this.currentTab = all?.find((tab) => tab.id === this.settings.tabId);
    });
  }

  ngAfterViewInit(): void {
    this.afterViewInitPromise.resolve();
    this.removeBackgroundWidget();
    this.setResizeEvent();
    this.setChangeEvent();
    setTimeout(() => {
      this.calcScrollHeight(this.elementRef.nativeElement.parentElement.clientWidth);
    }, 50);
  }

  ngOnDestroy(): void {
    this.removeKeyHandler();
    this.prevState = null;
    if (this.searchSession) {
      this.searchSession.destroy();
    }
  }

  initSort(widget: HomeTabs.CollectionWidget): boolean {
    const changed = !isEqual(this.currentSort, widget?.sort);
    if (changed) {
      this.currentSort = widget?.sort;
    }
    return changed;
  }

  private initData(forceLoadData = false) {
    this.layoutMode = 'list';
    this.movedWidget = this.isOwnerOrAdmin;
    if (isLayoutWidget(this.settings)) {
      const widget: HomeTabs.LayoutWidget = this.settings;
      this.layoutMode = widget?.layout ? widget.layout : 'list';
      this.initSort(widget);
    }
    if (this.settings || forceLoadData) {
      this.loadData();
    }
    this.cdr.markForCheck();
  }

  private removeBackgroundWidget() {
    const parentElement = this.widgetRef?.nativeElement?.parentElement?.parentElement;
    if (this.settings.type === 'header' && parentElement) {
      parentElement.classList.add('no-background-item');
      parentElement.parentElement?.classList.add('full-margin');
    }
  }

  protected abstract loadData(): void;

  private async calcScrollHeight(widthWidget: number) {
    await this.afterViewInitPromise;
    if (!this.heightWidget) {
      this.heightWidget = this.elementRef.nativeElement.parentElement.clientHeight;
    }
    if (this.heightWidget > 0) this.scrollHeight = `${this.heightWidget - 64}px`; // 64: header
    this.widgetResizeChange$.next(widthWidget);
    this.cdr.markForCheck();
  }

  protected onResizeChange(widthWidget?: number) {
    return this.calcScrollHeight(widthWidget);
  }

  private setRowData() {
    this.displayedContext = {
      id: 0,
      items: [],
      lastHeaderIndex: 0,
      clientSearchId: null,
      sessionId: null,
    };
    this.selectedIndex = undefined;
  }

  private removeKeyHandler() {
    if (this.keyHandlerId) {
      this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
      this.keyHandlerId = null;
    }
  }

  private setChangeEvent() {
    this.widgetsService
      .getChange$(this.settings.id)
      .pipe(untilDestroyed(this))
      .subscribe((s) => {
        const { id, ...viewSetting } = s;
        this.settings.viewSetting = { ...this.settings.viewSetting, ...viewSetting };
      });
  }

  private setResizeEvent() {
    const cb = async () => {
      await this.afterViewInitPromise;
      const width: number = this.elementRef.nativeElement.parentElement.clientWidth;
      this.isSmallScreen = width <= this.minWidgetSize; //min widget size
      this.heightWidget = this.elementRef.nativeElement.parentElement.clientHeight;
      await this.onResizeChange(width);
      this.cdr.markForCheck();
    };
    this.widgetsService
      .getResize$(this.settings.id)
      .pipe(untilDestroyed(this))
      .subscribe(() => cb());
    windowSizeObserver(50)
      .pipe(untilDestroyed(this))
      .subscribe(() => cb());
  }

  protected setFocusWidget(selectedIndex?: number) {
    if (this.selectedIndex === undefined || this.selectedIndex === null) {
      this.updateSelectedIndex(selectedIndex || 0);
    }
    this.keyboardHelperService.onInit(this.selectedIndex, this.displayedContext.items, this.scrollService);
    this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
    this.keyHandlerId = this.keyboardService.registerKeyHandler((keys, event) => {
      this.keyboardHelperService.handleArrows(keys, event);
      if (['ArrowDown', 'ArrowUp'].includes(keys[0])) this.keyboardOn = true;
      this.handleRowMenuKeys(keys, event);
    });
    this.cdr.markForCheck();
  }

  protected setUpKeyboardHelperService() {
    this.keyboardHelperService = new KeyboardHelperService();
    this.keyboardHelperService.updateCdr.pipe(untilDestroyed(this)).subscribe(() => this.cdr.markForCheck());
    this.keyboardHelperService.updateSelectedIndex.pipe(untilDestroyed(this)).subscribe((index) => {
      this.selectedIndex = index;
      this.prevState.selectedIndex = index;
      this.cdr.markForCheck();
    });
  }

  protected updateSelectedIndex(val: number) {
    this.selectedIndex = val;
    this.keyboardHelperService.selectedIndex = val;
    this.prevState.selectedIndex = val;
  }

  protected focusWidget(val: number) {
    this.updateSelectedIndex(val);
    this.widgetsService.triggerWidgetFocus$.next(this.settings.id);
  }

  protected handleRowMenuKeys(keys: Array<KeyName>, event: CustomKeyboardEvent): void {
    if (!this.resultElementsComponents.toArray().length) return;
    const selectedItem = this.resultElementsComponents.toArray().find((i) => i.index === this.selectedIndex);

    if (selectedItem && 'shortcutsHandlers' in selectedItem) {
      if (isMac()) keys = keys.map((k) => (k = k === 'control' ? 'command' : k));
      const matchingItem = selectedItem.shortcutsHandlers.find(
        (item) => item?.keyBinding && item?.keyBinding?.length === keys.length && item.keyBinding.every((key) => keys.includes(key))
      );

      if (matchingItem) {
        matchingItem.fn(selectedItem, 'keyboard');
        event.stopPropagation();
        return;
      }
    }
  }

  protected async openContextMenu(event: { x: number; y: number }) {
    this.focusWidget(this.selectedIndex);
    const items = await this.buildContextMenuItems();
    this.contextMenuOpened = true;
    this.activeContextMenu = this.contextMenuService.open(
      event,
      {
        items,
        onInvoke: (item: ContextMenuItem) => this.handleContextMenuCommand(item),
      },
      { position: 'below' }
    );
    this.cdr.markForCheck();
    this.activeContextMenu.destroy$.pipe(untilDestroyed(this)).subscribe(() => {
      this.contextMenuOpened = false;
      this.cdr.markForCheck();
    });
  }

  protected buildContextMenuItems(): ContextMenuItem[] {
    const contextMenuItems: ContextMenuItem[] = [
      {
        id: 'seeAll',
        text: 'See All',
        command: { type: 'see-all', value: this.pageType } as Commands.DynamicCommand,
      },
    ];
    if (this.canEdit) {
      contextMenuItems.push({
        id: 'layout',
        text: `Change to ${this.layoutMode === 'gallery' ? 'List' : 'Gallery'} View`,
        command: { type: 'change-layout', value: this.layoutMode } as Commands.DynamicCommand,
        icon: { type: 'font-icon', value: this.layoutMode === 'list' ? 'icon-gallery-view' : 'icon-list-view' },
      });
    }
    return contextMenuItems;
  }

  protected handleContextMenuCommand(command: ContextMenuItem) {
    if (command.id === 'layout') {
      this.updateSettingWidget(
        {
          ...this.settings,
          layout: this.layoutMode === 'gallery' ? 'list' : 'gallery',
        } as HomeTabs.LayoutWidget,
        { layout: true }
      );
    }
    if (command.id === 'seeAll') {
      this.navigate();
    }
  }

  protected navigate() {
    this.eventsService.event('home_page.widget_title_see_all', { label: this.pageType });
    this.routerService.navigateByUrl(this.pageType);
  }

  protected updateSort(searchSort?: Search.Sort) {
    this.updateSettingWidget(
      {
        ...this.settings,
        sort: searchSort,
      } as HomeTabs.LayoutWidget,
      { sort: true }
    );
  }

  protected async updateSettingWidget(updateItem: HomeTabs.Widget, updatedOption: UpdatedWidgetOption, displayWarningPopup?: boolean) {
    if (this.settings.saveStateImmediate || this.saveStateImmediate || !displayWarningPopup) {
      this.widgetsService.updateSettingWidget(updateItem);
      this.updateView(updateItem, updatedOption);
      return;
    }
    this.openWarningPopup(updateItem, updatedOption);
  }

  private updateView(updateItem: HomeTabs.LayoutWidget, updatedOption: UpdatedWidgetOption) {
    const { sort, layout } = updatedOption || {};
    if (layout) {
      this.layoutMode = updateItem.layout;
    }
    if (sort) {
      this.currentSort = updateItem.sort;
      this.loadData();
    }
    this.cdr.markForCheck();
  }

  private openWarningPopup(updateItem: HomeTabs.Widget, updatedOption: UpdatedWidgetOption) {
    if (this.warningPopupRef) {
      this.closeWarningPopup();
    }
    this.saveStateImmediate = false;
    this.warningPopupRef = this.popupService.open<AppPopupComponent, AppPopupData>(
      'center',
      AppPopupComponent,
      { ...this.WARNING_DATA, templateContent: this.warningPopupContent },
      {
        position: 'center',
      }
    );
    this.warningPopupRef.compInstance.primaryButton.pipe(takeUntil(this.warningPopupRef.destroy$)).subscribe(async () => {
      this.widgetsService.updateSettingWidget({ ...updateItem, saveStateImmediate: this.saveStateImmediate });
      this.updateView(updateItem, updatedOption);
      this.closeWarningPopup();
    });
    this.warningPopupRef.compInstance.secondaryButton.pipe(takeUntil(this.warningPopupRef.destroy$)).subscribe(() => {
      this.closeWarningPopup();
    });
  }

  private closeWarningPopup() {
    this.warningPopupRef?.destroy();
    this.warningPopupRef = null;
  }
}
